/**
 * @author mschuetz / http://mschuetz.at
 *
 * adapted from THREE.OrbitControls by
 *
 * @author qiao / https://github.com/qiao
 * @author mrdoob / http://mrdoob.com
 * @author alteredq / http://alteredqualia.com/
 * @author WestLangley / http://github.com/WestLangley
 * @author erich666 / http://erichaines.com
 *
 *
 *
 */

import * as THREE from 'three';
import { MOUSE } from '../defines';
import { EventDispatcher, Listener } from '../EventDispatcher';
import { Utils, type PotreePointCloud } from '../utils';

/** Global TWEEN reference from potree's script-loaded tween.js */
declare const TWEEN: {
  Tween: new (target: Record<string, number>) => TweenInstance;
  Easing: { Quartic: { Out: unknown }; [key: string]: unknown };
};

interface TweenInstance {
  to: (target: Record<string, number>, duration: number) => TweenInstance;
  easing: (easing: unknown) => TweenInstance;
  onUpdate: (callback: () => void) => TweenInstance;
  onComplete: (callback: () => void) => TweenInstance;
  start: () => TweenInstance;
  stop: () => void;
}

/** Loose interface for Potree viewer used by FirstPersonControls */
interface FPCViewer {
  renderer: THREE.WebGLRenderer;
  pRenderer: {
    renderOctree(octree: unknown, nodes: unknown[], camera: THREE.Camera, target: THREE.WebGLRenderTarget | null, params?: Record<string, unknown>): void;
  };
  scene: FPCScene;
  inputHandler: { mouse: THREE.Vector2; pressedKeys: Record<number, boolean> };
  setMoveSpeed(speed: number): void;
  getMoveSpeed(): number;
}

interface PotreeView {
  position: THREE.Vector3;
  radius: number;
  direction: THREE.Vector3;
  yaw: number;
  pitch: number;
  lookAt(target: THREE.Vector3): void;
  translate(x: number, y: number, z: number): void;
  translateWorld(x: number, y: number, z: number): void;
  clone(): PotreeView;
}

interface FPCScene {
  getActiveCamera(): THREE.Camera;
  view: PotreeView;
  pointclouds: PotreePointCloud[];
}

interface PointCloudIntersection {
  location: THREE.Vector3;
  pointcloud: {
    nodesOnRay(visibleNodes: unknown[], ray: THREE.Ray): { getBoundingSphere(sphere: THREE.Sphere): THREE.Sphere }[];
    visibleNodes: unknown[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export class FirstPersonControls extends EventDispatcher {
  viewer: FPCViewer;
  renderer: THREE.WebGLRenderer;
  enabled: boolean;
  scene: FPCScene;
  sceneControls: THREE.Scene;
  rotationSpeed: number;
  moveSpeed: number;
  lockElevation: boolean;
  keys: {
    FORWARD: number[];
    BACKWARD: number[];
    LEFT: number[];
    RIGHT: number[];
    UP: number[];
    DOWN: number[];
  };
  fadeFactor: number;
  yawDelta: number;
  pitchDelta: number;
  translationDelta: THREE.Vector3;
  translationWorldDelta: THREE.Vector3;
  tweens: TweenInstance[];

  constructor(viewer: FPCViewer) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;
    this.enabled = true;

    this.scene = null as unknown as FPCScene;
    this.sceneControls = new THREE.Scene();

    this.rotationSpeed = 200;
    this.moveSpeed = 10;
    this.lockElevation = false;

    this.keys = {
      FORWARD: ['W'.charCodeAt(0), 38],
      BACKWARD: ['S'.charCodeAt(0), 40],
      LEFT: ['A'.charCodeAt(0), 37],
      RIGHT: ['D'.charCodeAt(0), 39],
      UP: ['R'.charCodeAt(0), 33],
      DOWN: ['F'.charCodeAt(0), 34],
    };

    this.fadeFactor = 50;
    this.yawDelta = 0;
    this.pitchDelta = 0;
    this.translationDelta = new THREE.Vector3(0, 0, 0);
    this.translationWorldDelta = new THREE.Vector3(0, 0, 0);

    this.tweens = [];

    const drag = (e: { drag: { startHandled?: boolean; lastDrag: { x: number; y: number }; mouse: number } }) => {
      if (e.drag.startHandled === undefined) {
        e.drag.startHandled = true;

        this.dispatchEvent({ type: 'start' });
      }

      const moveSpeed = this.viewer.getMoveSpeed();

      const ndrag = {
        x: e.drag.lastDrag.x / this.renderer.domElement.clientWidth,
        y: e.drag.lastDrag.y / this.renderer.domElement.clientHeight,
      };

      if (e.drag.mouse === MOUSE.LEFT) {
        this.yawDelta += ndrag.x * this.rotationSpeed;
        this.pitchDelta += ndrag.y * this.rotationSpeed;
      } else if (e.drag.mouse === MOUSE.RIGHT) {
        this.translationDelta.x -= ndrag.x * moveSpeed * 100;
        this.translationDelta.z += ndrag.y * moveSpeed * 100;
      }
    };

    const drop = (_e: unknown) => {
      this.dispatchEvent({ type: 'end' });
    };

    const scroll = (e: { delta: number }) => {
      let speed = this.viewer.getMoveSpeed();

      if (e.delta < 0) {
        speed = speed * 0.9;
      } else if (e.delta > 0) {
        speed = speed / 0.9;
      }

      speed = Math.max(speed, 0.1);

      this.viewer.setMoveSpeed(speed);
    };

    const dblclick = (e: { mouse: THREE.Vector2 }) => {
      this.zoomToLocation(e.mouse);
    };

    this.addEventListener('drag', drag as unknown as Listener);
    this.addEventListener('drop', drop as unknown as Listener);
    this.addEventListener('mousewheel', scroll as unknown as Listener);
    this.addEventListener('dblclick', dblclick as unknown as Listener);
  }

  setScene(scene: FPCScene) {
    this.scene = scene;
  }

  stop() {
    this.yawDelta = 0;
    this.pitchDelta = 0;
    this.translationDelta.set(0, 0, 0);
  }

  zoomToLocation(mouse: THREE.Vector2) {
    const camera = this.scene.getActiveCamera();

    const I = Utils.getMousePointCloudIntersection(mouse, camera, this.viewer, this.scene.pointclouds) as PointCloudIntersection | null;

    if (I === null) {
      return;
    }

    let targetRadius = 0;
    {
      const minimumJumpDistance = 0.2;

      const domElement = this.renderer.domElement;
      const ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);

      const nodes = I.pointcloud.nodesOnRay(I.pointcloud.visibleNodes, ray);
      const lastNode = nodes[nodes.length - 1];
      const radius = lastNode.getBoundingSphere(new THREE.Sphere()).radius;
      targetRadius = Math.min(this.scene.view.radius, radius);
      targetRadius = Math.max(minimumJumpDistance, targetRadius);
    }

    const d = this.scene.view.direction.multiplyScalar(-1);
    const cameraTargetPosition = new THREE.Vector3().addVectors(I.location, d.multiplyScalar(targetRadius));
    // TODO Unused: let controlsTargetPosition = I.location;

    const animationDuration = 600;
    const easing = TWEEN.Easing.Quartic.Out;

    {
      // animate
      const value = { x: 0 };
      const tween = new TWEEN.Tween(value).to({ x: 1 }, animationDuration);
      tween.easing(easing);
      this.tweens.push(tween);

      const startPos = this.scene.view.position.clone();
      const targetPos = cameraTargetPosition.clone();
      const startRadius = this.scene.view.radius;
      const tweenTargetRadius = cameraTargetPosition.distanceTo(I.location);

      tween.onUpdate(() => {
        const t = value.x;
        this.scene.view.position.x = (1 - t) * startPos.x + t * targetPos.x;
        this.scene.view.position.y = (1 - t) * startPos.y + t * targetPos.y;
        this.scene.view.position.z = (1 - t) * startPos.z + t * targetPos.z;

        this.scene.view.radius = (1 - t) * startRadius + t * tweenTargetRadius;
        this.viewer.setMoveSpeed(this.scene.view.radius / 2.5);
      });

      tween.onComplete(() => {
        this.tweens = this.tweens.filter((e) => e !== tween);
      });

      tween.start();
    }
  }

  update(delta: number) {
    const view = this.scene.view;

    {
      // cancel move animations on user input
      const changes = [this.yawDelta, this.pitchDelta, this.translationDelta.length(), this.translationWorldDelta.length()];
      const changeHappens = changes.some((e) => Math.abs(e) > 0.001);
      if (changeHappens && this.tweens.length > 0) {
        this.tweens.forEach((e) => e.stop());
        this.tweens = [];
      }
    }

    {
      // accelerate while input is given
      const ih = this.viewer.inputHandler;

      const moveForward = this.keys.FORWARD.some((e) => ih.pressedKeys[e]);
      const moveBackward = this.keys.BACKWARD.some((e) => ih.pressedKeys[e]);
      const moveLeft = this.keys.LEFT.some((e) => ih.pressedKeys[e]);
      const moveRight = this.keys.RIGHT.some((e) => ih.pressedKeys[e]);
      const moveUp = this.keys.UP.some((e) => ih.pressedKeys[e]);
      const moveDown = this.keys.DOWN.some((e) => ih.pressedKeys[e]);

      if (this.lockElevation) {
        const dir = view.direction;
        dir.z = 0;
        dir.normalize();

        if (moveForward && moveBackward) {
          this.translationWorldDelta.set(0, 0, 0);
        } else if (moveForward) {
          this.translationWorldDelta.copy(dir.multiplyScalar(this.viewer.getMoveSpeed()));
        } else if (moveBackward) {
          this.translationWorldDelta.copy(dir.multiplyScalar(-this.viewer.getMoveSpeed()));
        }
      } else {
        if (moveForward && moveBackward) {
          this.translationDelta.y = 0;
        } else if (moveForward) {
          this.translationDelta.y = this.viewer.getMoveSpeed();
        } else if (moveBackward) {
          this.translationDelta.y = -this.viewer.getMoveSpeed();
        }
      }

      if (moveLeft && moveRight) {
        this.translationDelta.x = 0;
      } else if (moveLeft) {
        this.translationDelta.x = -this.viewer.getMoveSpeed();
      } else if (moveRight) {
        this.translationDelta.x = this.viewer.getMoveSpeed();
      }

      if (moveUp && moveDown) {
        this.translationWorldDelta.z = 0;
      } else if (moveUp) {
        this.translationWorldDelta.z = this.viewer.getMoveSpeed();
      } else if (moveDown) {
        this.translationWorldDelta.z = -this.viewer.getMoveSpeed();
      }
    }

    {
      // apply rotation
      let yaw = view.yaw;
      let pitch = view.pitch;

      yaw -= this.yawDelta * delta;
      pitch -= this.pitchDelta * delta;

      view.yaw = yaw;
      view.pitch = pitch;
    }

    // apply translation
    view.translate(this.translationDelta.x * delta, this.translationDelta.y * delta, this.translationDelta.z * delta);

    view.translateWorld(this.translationWorldDelta.x * delta, this.translationWorldDelta.y * delta, this.translationWorldDelta.z * delta);

    // set view target according to speed
    view.radius = 3 * this.viewer.getMoveSpeed();

    // decelerate over time
    const attenuation = Math.max(0, 1 - this.fadeFactor * delta);
    this.yawDelta *= attenuation;
    this.pitchDelta *= attenuation;
    this.translationDelta.multiplyScalar(attenuation);
    this.translationWorldDelta.multiplyScalar(attenuation);
  }
}
